import { FormProvider, useForm } from "react-hook-form";
import {
  ParticipantPlaceholder,
  useMediaDeviceSelect,
  usePreviewTracks,
} from "@livekit/components-react";
import { IconVolume } from "@tabler/icons-react";
import { facingModeFromLocalTrack, Track } from "livekit-client";
import { useEffect, useMemo, useRef } from "react";
import { Toggle } from "@shared/ui/toggle/toggle";
import { audioBitrateOptions } from "./conference-settings.constants";
import { DeviceSelect, SelectRow, VolumeSlider } from "./conference-settings-controls";
import s from "./conference-settings-modal.module.scss";

export const SoundSection = ({
  userChoices,
  saveAudioInputDeviceId,
  microphoneVolume,
  speakerVolume,
  setMicrophoneVolume,
  setSpeakerVolume,
  microphoneTrack,
  selectedAudioBitrate,
  setSelectedAudioBitrate,
  selectedAudioOutputDeviceId,
  setSelectedAudioOutputDeviceId,
  hasAdvancedAudioSettings,
  signalSettingsForm,
  supportedConstraints,
  echoCancellationEnabled,
  isApplyingAudio,
  audioSettingsError,
  lastMicrophoneError,
}) => {
  return (
    <>
      <div className={s.contentHeader}>
        <div>
          <div className={s.contentTitle}>Звук</div>
          <div className={s.contentSubtitle}>
            Устройства, музыкальные профили и точная настройка сигнала для конференции.
          </div>
        </div>
        <div className={s.contentBadge}>
          <IconVolume size={18} />
          <span>Аудио</span>
        </div>
      </div>

			<div className={s.cardGroup}>
        <div className={s.groupTitle}>Качество аудио</div>
        <SelectRow
          label="Качество передачи"
          caption={
            audioBitrateOptions.find((option) => option.value === selectedAudioBitrate)?.description
          }
          value={selectedAudioBitrate}
          options={audioBitrateOptions}
          onChange={setSelectedAudioBitrate}
          sectionClassName={s.settingRowCard}
        />
      </div>

      <div className={s.cardGroup}>
        <div className={s.groupTitle}>Устройства</div>
        <div className={s.inlineNote}>
          Проверка микрофона и динамика работает локально. Настройки звука и обработки будут
          применены только во время конференции.
        </div>
				<div className={s.deviceGroupCard}>
					<DeviceSelect
						label="Динамик"
						kind="audiooutput"
						value={selectedAudioOutputDeviceId}
						onPersist={setSelectedAudioOutputDeviceId}
						sectionClassName={s.settingRowCard}
						volume={speakerVolume}
					/>
					<VolumeSlider
						label="Громкость динамика"
						value={speakerVolume}
						onChange={setSpeakerVolume}
					/>
				</div>
				<div className={s.deviceGroupCard}>
					<DeviceSelect
						label="Микрофон"
						kind="audioinput"
						value={userChoices.audioDeviceId}
						onPersist={saveAudioInputDeviceId}
            track={microphoneTrack}
						sectionClassName={s.settingRowCard}
					/>
					<VolumeSlider
						label="Громкость микрофона"
						value={microphoneVolume}
						onChange={setMicrophoneVolume}
					/>
				</div>
      </div>

      {hasAdvancedAudioSettings ? (
        <>
          <div className={s.cardGroup}>
            <div className={s.groupTitle}>Обработка сигнала</div>
            <FormProvider {...signalSettingsForm}>
              <div className={s.signalCheckboxGrid}>
                {supportedConstraints.noiseSuppression ? (
                  <Toggle label={"Шумоподавление"} name="noiseSuppression" />
                ) : null}
                {supportedConstraints.echoCancellation ? (
                  <Toggle label={"Эхоподавление"} name="echoCancellation" />
                ) : null}
                {supportedConstraints.autoGainControl ? (
                  <Toggle label={"Автоматическая регулировка усиления"} name="autoGainControl" />
                ) : null}
              </div>
            </FormProvider>

            {echoCancellationEnabled ? (
              <div className={s.inlineNote}>
                Эхоподавление включено. Для уроков музыки лучше использовать наушники, иначе
                алгоритм может съедать атаку и обертона.
              </div>
            ) : null}
            {isApplyingAudio ? <div className={s.inlineNote}>Сохраняем настройки звука...</div> : null}
            {audioSettingsError ? <div className={s.inlineNote}>{audioSettingsError}</div> : null}
            {lastMicrophoneError ? <div className={s.inlineError}>{lastMicrophoneError.message}</div> : null}
          </div>
        </>
      ) : null}
    </>
  );
};

export const VideoSection = ({
  userChoices,
  saveVideoInputDeviceId,
  saveVideoInputEnabled,
}) => {
  const videoSettingsForm = useForm({
    mode: "onChange",
    defaultValues: {
      joinWithoutCamera: !userChoices.videoEnabled,
      showSelfOnMeeting: true,
      hideParticipantsVideo: false,
    },
  });

  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: "videoinput",
    requestPermissions: true,
  });

  const selectedVideoDeviceId = activeDeviceId || userChoices.videoDeviceId || "";
  const joinWithoutCamera = videoSettingsForm.watch("joinWithoutCamera");
  const showSelfOnMeeting = videoSettingsForm.watch("showSelfOnMeeting");
  const previewVideoEnabled = !joinWithoutCamera && showSelfOnMeeting;
  const videoElementRef = useRef(null);

  const previewTracks = usePreviewTracks(
    useMemo(
      () => ({
        audio: false,
        video: previewVideoEnabled
          ? {
              deviceId: selectedVideoDeviceId || undefined,
            }
          : false,
      }),
      [previewVideoEnabled, selectedVideoDeviceId]
    )
  );

  const previewVideoTrack = useMemo(
    () => previewTracks?.find((track) => track.kind === Track.Kind.Video),
    [previewTracks]
  );

  const previewFacingMode = useMemo(() => {
    if (!previewVideoTrack) {
      return "undefined";
    }

    const { facingMode } = facingModeFromLocalTrack(previewVideoTrack);
    return facingMode;
  }, [previewVideoTrack]);

  useEffect(() => {
    if (videoElementRef.current && previewVideoTrack) {
      previewVideoTrack.unmute();
      previewVideoTrack.attach(videoElementRef.current);
    }

    return () => {
      previewVideoTrack?.detach();
    };
  }, [previewVideoTrack]);

  useEffect(() => {
    const subscription = videoSettingsForm.watch((value, { name }) => {
      if (name === "joinWithoutCamera") {
        saveVideoInputEnabled(!value.joinWithoutCamera);
      }
    });

    return () => subscription.unsubscribe();
  }, [saveVideoInputEnabled, videoSettingsForm]);

  return (
    <>
      <div className={s.contentHeader}>
        <div>
          <div className={s.contentTitle}>Видео</div>
          <div className={s.contentSubtitle}>
            Управление камерой и выбор устройства видеозахвата для конференции.
          </div>
        </div>
      </div>

      <div className={s.cardGroup}>
        <div className={s.cameraCard}>
							<div className={s.cameraPreviewInner}>
								{previewVideoTrack && previewVideoEnabled ? (
									<video
										ref={videoElementRef}
										className={s.cameraPreviewVideo}
										width="1280"
										height="720"
										data-lk-facing-mode={previewFacingMode}
									/>
								) : (
									<div className={s.cameraPreviewPlaceholder}>
										<ParticipantPlaceholder />
									</div>
								)}
							</div>

          <FormProvider {...videoSettingsForm}>
            <div className={s.cameraControls}>
              <div className={`${s.settingRow} ${s.settingRowCard}`}>
                <div className={s.settingMeta}>
                  <div className={s.settingLabel}>Камера</div>
                  <div className={s.settingCaption}>
                    {devices.length ? `${devices.length} устройств` : "Устройства не найдены"}
                  </div>
                </div>
                <select
                  className={`${s.select} ${s.cameraSelect}`}
                  value={selectedVideoDeviceId}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setActiveMediaDevice(nextValue);
                    saveVideoInputDeviceId(nextValue);
                  }}
                >
                  <option value="">
                    {devices.length ? "Выберите значение" : "Нет доступных устройств"}
                  </option>
                  {devices.map((device, index) => (
                    // deviceId бывает пустым до выдачи разрешения — иначе дублирующийся key ""
                    <option key={device.deviceId || `camera-device-${index}`} value={device.deviceId}>
                      {device.label || `Камера ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </FormProvider>
        </div>
      </div>
    </>
  );
};

export const SupportSection = ({
  availableAudioSettings,
  lastMicrophoneError,
  supportedConstraints,
  noiseSuppressionFallback,
}) => {
  return (
    <>
      <div className={s.contentHeader}>
        <div>
          <div className={s.contentTitle}>Справка и поддержка</div>
          <div className={s.contentSubtitle}>
            Диагностика браузерной поддержки и быстрые подсказки по работе конференции.
          </div>
        </div>
      </div>

      <div className={s.cardGroup}>
        <div className={s.groupTitle}>Поддерживаемые аудио-параметры</div>
        <div className={s.supportList}>
          {availableAudioSettings.length ? (
            availableAudioSettings.map((item) => (
              <div key={item} className={s.supportItem}>
                {item}
              </div>
            ))
          ) : (
            <div className={s.placeholderText}>
              Браузер не сообщил о поддержке дополнительных аудио-ограничений.
            </div>
          )}
        </div>
      </div>

      <div className={s.cardGroup}>
        <div className={s.groupTitle}>Подсказки</div>
        <div className={s.helpText}>
          Для музыки обычно лучше выключать автоматические улучшатели сигнала и работать через
          профиль инструмента. Если браузер не дает звук, сначала разреши автопроигрывание в
          разделе `Звук`, затем переподключись к комнате.
        </div>
        {lastMicrophoneError ? <div className={s.inlineError}>{lastMicrophoneError.message}</div> : null}
      </div>

      {!supportedConstraints.noiseSuppression && noiseSuppressionFallback ? (
        <div className={s.cardGroup}>
          <div className={s.groupTitle}>{noiseSuppressionFallback.title}</div>
          <div className={s.helpText}>
            Этот браузер не сообщает поддержку `noiseSuppression`. Для занятий на инструментах
            обычно лучше отключить системное шумоподавление, чтобы не съедать атаку, сустейн и
            тихие нюансы звука.
          </div>
          <div className={s.supportSteps}>
            {noiseSuppressionFallback.steps.map((step) => (
              <div key={step} className={s.supportStep}>
                {step}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};
